import React, { createContext, useState, useCallback, useEffect } from 'react';
import * as api from '@services/api';
import { setTwilioToken } from '@services/api';
import { isJwtExpired, msUntilExpiration } from '@utils/jwtUtils';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [jwtToken, setJwtToken] = useState(() => {
    const stored = localStorage.getItem('jwtToken');
    if (stored && isJwtExpired(stored)) {
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('twilioToken');
      return null;
    }
    return stored;
  });
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Listen for 401 unauthorized responses
  useEffect(() => {
    const handleUnauthorized = () => {
      setJwtToken(null);
      setUser(null);
      localStorage.removeItem('jwtToken');
    };

    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, []);

  // Schedule a precise expiration cutover so an idle tab redirects the moment
  // the current JWT expires, instead of waiting for the next API call.
  useEffect(() => {
    if (!jwtToken) return undefined;
    const ms = msUntilExpiration(jwtToken);
    if (ms === null || ms <= 0) return undefined;
    const timer = setTimeout(() => {
      setJwtToken(null);
      setUser(null);
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('twilioToken');
    }, ms);
    return () => clearTimeout(timer);
  }, [jwtToken]);

  // Login with email and password
  const login = useCallback(async (email, password) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.login(email, password);
      console.log('[AuthContext] Login API response:', response);
      console.log('[AuthContext] Full response payload:', JSON.stringify(response, null, 2));

      // Store JWT token from login response (authentication_token property)
      if (response.authentication_token) {
        console.log('[AuthContext] Found authentication_token:', response.authentication_token);
        setJwtToken(response.authentication_token);
        localStorage.setItem('jwtToken', response.authentication_token);
      } else {
        console.log('[AuthContext] No authentication_token found in response');
      }

      // Store Twilio token for Twilio device initialization
      console.log('[AuthContext] Checking for Twilio token...');
      console.log('[AuthContext] response.twilio:', response.twilio);
      if (response.twilio?.twilioToken) {
        if (response.twilio.twilioToken.trim() === '') {
          console.warn('[AuthContext] ⚠️ Twilio token is EMPTY STRING! Backend needs to generate a capability token');
        } else {
          console.log('[AuthContext] Found Twilio token:', response.twilio.twilioToken.substring(0, 50) + '...');
          setTwilioToken(response.twilio.twilioToken);
        }
      } else {
        console.log('[AuthContext] No Twilio token found at response.twilio?.twilioToken');
        console.log('[AuthContext] Available keys in response.twilio:', Object.keys(response.twilio || {}));
      }
      console.log('[AuthContext] Twilio data:', {
        twilioToken: response.twilio?.twilioToken ? '(present)' : '(missing)',
        from: response.twilio?.from,
        outgoingAppSid: response.twilio?.outgoingAppSid,
      });

      // Extract user info from response
      const userData = {
        id: response.id,
        email: response.email,
        firstName: response.first_name,
        lastName: response.last_name,
        role: response.role,
        twilioFrom: response.twilio?.from,
        defaultTestPatientGuid: response.twilio?.defaultTestPatientGuid,
        teamId: response.team_id,
        teamName: response.team_name,
        teamIsTesting: response.team_is_testing,
      };
      console.log('[AuthContext] User data extracted:', userData);
      setUser(userData);

      return { success: true, data: userData };
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Login failed';
      console.error('[AuthContext] Login error:', err);
      console.error('[AuthContext] Error details:', err.response?.data || err.message);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await api.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setJwtToken(null);
      setUser(null);
      localStorage.removeItem('jwtToken');
      setIsLoading(false);
    }
  }, []);

  const value = {
    jwtToken,
    user,
    isLoading,
    error,
    login,
    logout,
    isAuthenticated: !!jwtToken && !isJwtExpired(jwtToken),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
